import { motion } from "framer-motion"
import { useId } from "react"
import type { FormEvent } from "react"
import type { MessageChannel } from "../../types"

type ComposerProps = {
  disabled?: boolean
  channel: MessageChannel
  onChannelChange: (channel: MessageChannel) => void
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  pending?: boolean
  error?: string | null
  showDisabledBanner?: boolean
}

const CHANNEL_OPTIONS: Array<{ id: MessageChannel; label: string; disabled?: boolean }> = [
  { id: "sms", label: "SMS" },
  { id: "email", label: "Email", disabled: true },
]

export function ChatComposer({
  disabled,
  channel,
  onChannelChange,
  value,
  onChange,
  onSubmit,
  pending,
  error,
  showDisabledBanner,
}: ComposerProps) {
  const errorId = useId()

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (disabled || pending || !value.trim()) return
    onSubmit()
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: "grid", gap: "12px" }} aria-live="polite">
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
        {CHANNEL_OPTIONS.map((option) => (
          <motion.button
            key={option.id}
            type="button"
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              if (option.disabled) return
              onChannelChange(option.id)
            }}
            disabled={disabled || option.disabled}
            className="mono"
            style={{
              padding: "8px 14px",
              border: option.id === channel ? "2px solid var(--rail)" : "1px solid var(--rail)",
              background: option.id === channel ? "rgba(255,43,79,0.12)" : "transparent",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              cursor: disabled || option.disabled ? "not-allowed" : "pointer",
              opacity: option.disabled ? 0.5 : 1,
              minHeight: "40px",
            }}
            aria-pressed={option.id === channel}
          >
            {option.label}
            {option.disabled ? " (soon)" : ""}
          </motion.button>
        ))}
      </div>

      {showDisabledBanner ? (
        <div
          className="mono small"
          role="status"
          style={{
            border: "1px dashed rgba(255,43,79,0.4)",
            padding: "10px 12px",
            color: "var(--error-text)",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          Web send is disabled for this tenant.
        </div>
      ) : null}

      <div style={{ border: "1px solid var(--rail)" }}>
        <textarea
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={disabled ? "Enable web send in settings to reply" : "Type a message"}
          rows={4}
          disabled={disabled}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          aria-disabled={disabled}
          style={{
            width: "100%",
            padding: "14px",
            border: "none",
            background: "transparent",
            color: "var(--text)",
            fontFamily: '"IBM Plex Mono","Space Mono",ui-monospace,Menlo,Consolas,monospace',
          }}
        />
      </div>

      {error ? (
        <div className="mono small" role="alert" id={errorId} style={{ color: "var(--error-text)" }}>
          {error}
        </div>
      ) : null}

      <motion.button
        type="submit"
        whileTap={{ scale: 0.95 }}
        disabled={disabled || pending || !value.trim()}
        className="mono"
        style={{
          padding: "10px 16px",
          border: "2px solid var(--rail)",
          background: disabled ? "rgba(18,18,20,0.3)" : "transparent",
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          cursor: disabled || pending || !value.trim() ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
          minHeight: "44px",
        }}
      >
        {pending ? "Sending…" : "Send"}
      </motion.button>
    </form>
  )
}

export default ChatComposer
